// Discriminated-union schema for audience filter rows. Each filter type declares
// its supported operators, the expected value shape, a default value, and a
// hint about which ValueEditor the FilterRow should render.

import { LIMITS } from '../constants';

export const AUDIENCE_FILTER = {
  ROLE: 'role',
  ENROLLED_IN: 'enrolledIn',
  NOT_ENROLLED: 'notEnrolled',
  COMPLETED_COURSE: 'completedCourse',
  COURSE_CATEGORY: 'courseCategory',
  HAS_PURCHASED: 'hasPurchased',
  PURCHASE_AMOUNT: 'purchaseAmount',
  LAST_ACTIVE_BEFORE: 'lastActiveBefore',
  LAST_ACTIVE_AFTER: 'lastActiveAfter',
  EMAIL_DOMAIN: 'emailDomain',
  COMPLETION_RATE: 'completionRate',
  GROUP: 'group',
};

export const AUDIENCE_FILTER_SCHEMA = {
  [AUDIENCE_FILTER.ROLE]: {
    label: 'Role',
    editor: 'checkboxGroup',
    operators: ['in'],
    defaultValue: ['reader'],
    optionsKey: 'roles',
  },
  [AUDIENCE_FILTER.ENROLLED_IN]: {
    label: 'Enrolled in',
    editor: 'courseMultiSelect',
    operators: ['in'],
    defaultValue: [],
    maxValues: LIMITS.AUDIENCE_FILTER_VALUES_MAX,
    optionsKey: 'courses',
  },
  [AUDIENCE_FILTER.NOT_ENROLLED]: {
    label: 'Not enrolled in',
    editor: 'courseMultiSelect',
    operators: ['in'],
    defaultValue: [],
    maxValues: LIMITS.AUDIENCE_FILTER_VALUES_MAX,
    optionsKey: 'courses',
  },
  [AUDIENCE_FILTER.COMPLETED_COURSE]: {
    label: 'Completed course',
    editor: 'courseMultiSelect',
    operators: ['in'],
    defaultValue: [],
    maxValues: LIMITS.AUDIENCE_FILTER_VALUES_MAX,
    optionsKey: 'courses',
  },
  [AUDIENCE_FILTER.COURSE_CATEGORY]: {
    label: 'Course category',
    editor: 'categoryMultiSelect',
    operators: ['in'],
    defaultValue: [],
    maxValues: LIMITS.AUDIENCE_FILTER_VALUES_MAX,
    optionsKey: 'categories',
  },
  [AUDIENCE_FILTER.HAS_PURCHASED]: {
    label: 'Has purchased',
    editor: 'yesNo',
    operators: ['eq'],
    defaultValue: true,
  },
  [AUDIENCE_FILTER.PURCHASE_AMOUNT]: {
    label: 'Purchase amount',
    editor: 'numberRange',
    operators: ['gte', 'lte', 'between'],
    defaultValue: { min: null, max: null },
  },
  [AUDIENCE_FILTER.LAST_ACTIVE_BEFORE]: {
    label: 'Last active before',
    editor: 'date',
    operators: ['lt'],
    defaultValue: null,
  },
  [AUDIENCE_FILTER.LAST_ACTIVE_AFTER]: {
    label: 'Last active after',
    editor: 'date',
    operators: ['gt'],
    defaultValue: null,
  },
  [AUDIENCE_FILTER.EMAIL_DOMAIN]: {
    label: 'Email domain',
    editor: 'domainList',
    operators: ['in'],
    defaultValue: [],
  },
  [AUDIENCE_FILTER.COMPLETION_RATE]: {
    label: 'Course completion rate',
    editor: 'percentRange',
    operators: ['gte', 'lte', 'between'],
    defaultValue: { min: null, max: null },
    hint: 'Only applies to students enrolled in at least one course.',
  },
  [AUDIENCE_FILTER.GROUP]: {
    label: 'Group',
    editor: 'groupMultiSelect',
    operators: ['in'],
    defaultValue: [],
    optionsKey: 'groups',
  },
};

export const AUDIENCE_FILTERS = Object.values(AUDIENCE_FILTER);

export const createFilter = (type) => {
  const schema = AUDIENCE_FILTER_SCHEMA[type];
  if (!schema) throw new Error(`Unknown audience filter type: ${type}`);
  return {
    id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    operator: schema.operators[0],
    value: schema.defaultValue,
  };
};

// Flag mutually-exclusive filter combinations so the UI can warn inline.
export const detectConflicts = (filters = []) => {
  const conflicts = [];
  const enrolled = filters.filter((f) => f.type === AUDIENCE_FILTER.ENROLLED_IN);
  const notEnrolled = filters.filter((f) => f.type === AUDIENCE_FILTER.NOT_ENROLLED);
  for (const a of enrolled) {
    for (const b of notEnrolled) {
      const overlap = (a.value ?? []).some((id) => (b.value ?? []).includes(id));
      if (overlap) conflicts.push({ a, b, reason: 'course appears in both enrolled and not-enrolled' });
    }
  }
  return conflicts;
};

// Validate a single filter row in isolation — used by FilterRow before commit.
export const validateFilter = (filter) => {
  const schema = AUDIENCE_FILTER_SCHEMA[filter?.type];
  if (!schema) return { valid: false, message: 'Unknown filter type.' };
  const { value } = filter;
  if (schema.maxValues && Array.isArray(value) && value.length > schema.maxValues) {
    return { valid: false, message: `Up to ${schema.maxValues} values.` };
  }
  if (Array.isArray(value) && value.length === 0 && schema.editor !== 'yesNo') {
    return { valid: false, message: 'Pick at least one value.' };
  }
  return { valid: true };
};
